import React, { useState, useEffect, useContext, useRef } from "react";
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import axios from "axios";
import { withCsrfHeaders } from "../api/csrf";
import { useSnackbar } from "components/GlobalSnackbar";
import { handleApiError } from "utils/errorHandler";
import {  
  Grid,
  Card,
  TextField,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  Icon,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
} from "@mui/material";
import { generatePrintInboisAutoHtml } from "../components/STPrint/PrintInboisAuto";
import PrintIcon from '@mui/icons-material/Print';
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import { pageHeaderStyles } from "utils/pageHeaderStyles";
import { buttonStyles } from "utils/buttonStyles";

import { apiSearchZones } from "../api/zones";
import { apiSearchCategories } from "../api/categories";
import { getCurrentUser } from "../api/auth";
import { apiSearchAkaun } from "../api/akaun";
import { perkaraExists } from "../api/inboisLejarCheck";
import { apiGetInboisByMonthYearZon, apiGetInboisByDateRangeZon } from "../api/inbois";
import { autoCapitalizePayload } from "utils/autoCapitalize";
import { useDebounce } from "hooks/useDebounce";

// Helper to get perkara string for a row
function getPerkaraString(kategori, tarikhInbois) {
  if (!kategori || !tarikhInbois) return "";
  const d = new Date(tarikhInbois);
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  return `${kategori} ${mm}/${yyyy}`;
}
// Helper: format date as DD/MM/YYYY (fallback to first 10 chars if invalid)
function formatDDMMYYYY(dateStr) {
  const d = new Date(dateStr);
  if (isNaN(d)) {
    return String(dateStr).slice(0, 10);
  }
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yyyy = d.getFullYear();
  return `${dd}/${mm}/${yyyy}`;
}
// Helper to format 'YYYY-MM' to 'Bulan YYYY' in Malay
function printMonthYear(ym) {
  if (!ym) return '';
  const [year, month] = ym.split('-');
  const bulan = [
    '', 'Januari', 'Februari', 'Mac', 'April', 'Mei', 'Jun',
    'Julai', 'Ogos', 'September', 'Oktober', 'November', 'Disember'
  ];
  const m = parseInt(month, 10);
  return `${bulan[m]} ${year}`;
}
export default function AutoJanaInbois() {
  // Fetch zon options on mount
  useEffect(() => {
    async function fetchZonOptions() {
      try {
        const res = await apiSearchZones();
        if (res && res.data) {
          setZonOptions(res.data.map(z => z.zon || z.nama || z));
        }
      } catch (e) {
        setZonOptions([]);
      }
    }
    fetchZonOptions();
  }, []);

  // Fetch kategori options on mount
  useEffect(() => {
    async function fetchKategoriOptions() {
      try {
        const res = await apiSearchCategories({});
        if (res && res.data) {
          setKategoriOptions(res.data.map(k => k.kategori_kutipan || k.nama || k));
        }
      } catch (e) {
        setKategoriOptions([]);
      }
    }
    fetchKategoriOptions();
  }, []);
  // All useState declarations at the top
  const [statusJanaMap, setStatusJanaMap] = useState({});
  const [zonOptions, setZonOptions] = useState([]);
  const [printZon, setPrintZon] = useState("");
  const [kategoriOptions, setKategoriOptions] = useState([]);
  const [printKategori, setPrintKategori] = useState("");
  const [tarikhInbois, setTarikhInbois] = useState("");
  // Debounce tarikhInbois so rapid manual typing (YYYY-MM-DD) doesn't trigger repeated heavy checks
  const debouncedTarikhInbois = useDebounce(tarikhInbois, 300);
  const [previewList, setPreviewList] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const { showSuccess, showError, showInfo } = useSnackbar();
  const [isJanaLoading, setIsJanaLoading] = useState(false);
  // Track if preview has been loaded for current filter
  const [previewLoaded, setPreviewLoaded] = useState(false);
  // Track expired rows (key: no_akaun) => { tarikh_tamat }
  const [expiredMap, setExpiredMap] = useState({});
  // For Cetak Inbois section
  const [tarikhMula, setTarikhMula] = useState("");
  const [tarikhTamat, setTarikhTamat] = useState("");
  const [printZonOnly, setPrintZonOnly] = useState("");

  // ...existing code...

  // Helper to refresh status jana for all rows in previewList (batched to reduce burst load)
  const refreshStatusJana = async (rows, kategori, tarikh) => {
    if (!rows?.length || !kategori || !tarikh) {
      setStatusJanaMap({});
      return;
    }
    const capturedTarikh = tarikh; // for race protection
    const newMap = {};
    const batchSize = 20; // tune if needed
    const sleep = (ms) => new Promise(r => setTimeout(r, ms));
    for (let i = 0; i < rows.length; i += batchSize) {
      const slice = rows.slice(i, i + batchSize);
      await Promise.all(slice.map(async (row) => {
        const no_akaun = row.no_akaun || row.noakaun;
        const perkara = getPerkaraString(kategori, capturedTarikh);
        if (!no_akaun || !perkara) {
          newMap[`${no_akaun}|${perkara}`] = "Belum";
          return;
        }
        try {
          const res = await axios.get(`/api/inbois/exists`, { params: { no_akaun, perkara } });
          newMap[`${no_akaun}|${perkara}`] = res.data.exists ? "Sudah" : "Belum";
        } catch {
          newMap[`${no_akaun}|${perkara}`] = "Belum";
        }
      }));
      // Small delay between batches to avoid spike (skip after last batch)
      if (i + batchSize < rows.length) await sleep(120);
      // If tarikhInbois changed while we were processing, abort to avoid stale overwrite
      if (capturedTarikh !== (debouncedTarikhInbois || tarikhInbois)) return;
    }
    setStatusJanaMap(prev => {
      // If tarikh drifted, keep previous map
      if (capturedTarikh !== (debouncedTarikhInbois || tarikhInbois)) return prev;
      return newMap;
    });
  };

  // Batch handler: generate batch payload and send to backend
  const handleBulkJanaInbois = async () => {
    if (!previewList.length || !printKategori || !tarikhInbois) return;
    setIsJanaLoading(true);
    // Build batch payloads for selected rows
    const selectedRowObjs = previewList.filter((row, idx) => selectedRows.includes(row.no_akaun || idx));
    // Client-side guard: skip rows where tarikhInbois is after row.tarikh_tamat
    const invalid = [];
    const validRows = selectedRowObjs.filter(r => {
      const tamat = r.tarikh_tamat;
      if (!tamat) return true; // no end date means allowed
      try {
        return new Date(tarikhInbois) <= new Date(tamat);
      } catch { return true; }
    });
    selectedRowObjs.forEach(r => {
      const tamat = r.tarikh_tamat;
      if (tamat && new Date(tarikhInbois) > new Date(tamat)) {
        invalid.push({ no_akaun: r.no_akaun || r.noakaun, tarikh_tamat: tamat });
      }
    });
    if (validRows.length === 0 && invalid.length > 0) {
      setIsJanaLoading(false);
      const dateList = [...new Set(invalid.map(i => formatDDMMYYYY(i.tarikh_tamat)))];
      let dateSummary = '';
      if (dateList.length === 1) dateSummary = dateList[0];
      else if (dateList.length <= 4) dateSummary = dateList.join(', ');
      else dateSummary = dateList.slice(0,4).join(', ') + ` +${dateList.length-4} lagi`;
      showError(`Ralat. Semua akaun melepasi tarikh tamat sewaan. Tarikh tamat terlibat: ${dateSummary}`);
      return;
    }
    // Check if all selected rows already have status jana 'Sudah'
    const allSudah = selectedRowObjs.length > 0 && selectedRowObjs.every(row => {
      const no_akaun = row.no_akaun || row.noakaun;
      const perkara = getPerkaraString(printKategori, tarikhInbois);
      const key = `${no_akaun}|${perkara}`;
      return statusJanaMap[key] === 'Sudah';
    });
    if (allSudah) {
      setIsJanaLoading(false);
      showError('Inbois pada bulan terpilih sudah dijana');
      return;
    }
    // Fetch max no_inbois starting with 4
    let maxNoInbois = 0;
    try {
      const api = await import('../api/inbois');
      const maxNo = await api.apiGetMaxNoInboisLike4();
      maxNoInbois = parseInt(maxNo, 10) || 4000000;
    } catch (e) {
      maxNoInbois = 4000000;
    }
    // Generate new no_inbois for each row and build lejar payloads
    const today = new Date();
    const todayStr = today.toISOString().slice(0, 10); // YYYY-MM-DD
    const user = getCurrentUser();
    const batchPayloads = [];
    const lejarPayloads = [];
    validRows.forEach((row, i) => {
      const newNoInbois = String(maxNoInbois + 1 + i);
      // Inbois payload (dropped columns removed: no_akaun_lama, nama, alamat1_pelanggan, alamat1_harta, no_kp, kategori_kutipan, kod_pelanggan, zon)
  batchPayloads.push({
        id_inbois_bahagian: 0,
        id_akaun: 0,
        no_akaun: row.no_akaun || row.noakaun,
        no_inbois: newNoInbois,
        kod_harta: row.kodharta || row.kod_harta || "",
        bil_hari_lewat_bayar: 0,
        notis_pertama: 0,
        status_notis_pertama: 0,
        notis_kedua: 0,
        status_notis_kedua: 0,
        notis_ketiga: 0,
        status_notis_ketiga: 0,
        the_diff: 0,
        aging_till6: 0,
        aging7_12: 0,
        aging13_18: 0,
        aging19_24: 0,
        aging25_30: 0,
        aging31_36: 0,
        aging37_72: 0,
        aging73_120: 0,
        aging_more120: 0,
        no_resit: "",
        kategori_inbois: "Auto",
        tunggakan_to_date: 0.0,
        denda_to_date: 0.0,
        baki_akhir_lejar: 0.0,
        peratus_diskaun: 0.0,
        mark_bayar: 0,
        mark_post: 1,
        jumlah_bayar: 0.0,
        tarikh_inbois: tarikhInbois,
        tarikh_simpan: todayStr,
        perkara: getPerkaraString(printKategori, tarikhInbois),
        jumlah: row.kadar_sewa || 0,
        bahagian_pemantau: user?.bahagian || "",
        jumlah_total_detail: row.kadar_sewa || 0,
        id_pengguna: (user?.id_pengguna || "").toLowerCase(),
        bahagian_pemantau: user?.bahagian || "",
      });
      // Lejar payload
      lejarPayloads.push({
        id_akaun: 0,
        no_akaun: row.no_akaun || row.noakaun,
        kod_harta: row.kodharta || row.kod_harta || row.no_akaun || row.noakaun,
        tarikh_inbois: tarikhInbois,
        tarikh_simpan: todayStr,
        tarikh_resit: tarikhInbois,
        no_resit: "",
        doc_date: null,
        doc_date1: null,
        doc_no: "",
        no_inbois: newNoInbois,
        perkara: `INV: ${getPerkaraString(printKategori, tarikhInbois)}`,
        debit: row.kadar_sewa || 0,
        kredit: 0.0,
        baki: 0.0,
        baki_asal: 0.0,
        id_pengguna: (user?.id_pengguna || "").toLowerCase(),
        bahagian_pemantau: user?.bahagian || "",
      });
    });
  let successCount = 0;
    let failCount = 0;
    let backendErrorMsg = "";
    try {
      // Ensure CSRF token and session cookie are sent (backend requires X-CSRF-Token for mutating /api)
      const headers = await withCsrfHeaders({
        "Content-Type": "application/json",
        "X-User-Department": user?.bahagian || "",
      });
      const res = await fetch('/api/bulk-inbois-lejar/bulk-lejar', {
        method: 'POST',
        headers,
        credentials: 'include',
        body: JSON.stringify({
          inboisList: batchPayloads,
          lejarList: lejarPayloads,
        }),
      });
      if (!res.ok) {
        const errBody = await res.json().catch(() => ({}));
        throw new Error(errBody.error || `HTTP ${res.status}`);
      }
      const data = await res.json();
  const results = data.results || [];
      successCount = results.filter(r => r.status === 'success').length;
  failCount = results.length - successCount;
  // Append client-side invalids to failure count for messaging
  failCount += invalid.length;
      // If all failed, try to extract error from first result
      if (successCount === 0 && results.length > 0 && results[0].error) {
        backendErrorMsg = results[0].error;
      }
    } catch (err) {
      failCount = batchPayloads.length;
      successCount = 0;
      if (err.response && err.response.data && err.response.data.error) {
        backendErrorMsg = err.response.data.error;
      } else if (err.message) {
        backendErrorMsg = err.message;
      } else {
        backendErrorMsg = "Ralat tidak diketahui dari pelayan.";
      }
    }
    // Determine snackbar color and message
    let snackbarSeverity = "success";
    let snackbarMsg = "";
    if (successCount === selectedRowObjs.length && successCount > 0) {
      snackbarSeverity = "success";
      snackbarMsg = `${successCount} inbois berjaya dijana.`;
    } else if (successCount === 0) {
      snackbarSeverity = "error";
      snackbarMsg = backendErrorMsg || `${failCount} inbois gagal dijana.`;
    } else {
      snackbarSeverity = "warning";
      snackbarMsg = `${successCount} berjaya dijana, ${failCount} gagal/ditolak.`;
    }
    // Append invalid/expired details if any (partial case)
    if (invalid.length > 0) {
      const akaunsInvalid = invalid.map(i => i.no_akaun);
      const akaunStr = akaunsInvalid.length <= 5 ? akaunsInvalid.join(', ') : akaunsInvalid.slice(0,5).join(', ') + ` +${akaunsInvalid.length-5} lagi`;
      const dateSet = [...new Set(invalid.map(i => formatDDMMYYYY(i.tarikh_tamat)))];
      const dateStr = dateSet.length === 1 ? dateSet[0] : (dateSet.length <= 4 ? dateSet.join(', ') : dateSet.slice(0,4).join(', ') + ` +${dateSet.length-4} lagi`);
      snackbarMsg += ` (Lewat tamat: ${akaunStr}; Tarikh tamat: ${dateStr})`;
    }
    setIsJanaLoading(false);
    if (snackbarSeverity === 'success') {
      showSuccess(snackbarMsg);
    } else if (snackbarSeverity === 'error') {
      showError(snackbarMsg);
    } else if (snackbarSeverity === 'warning') {
      showInfo(snackbarMsg);
    }
    // Refresh status jana for all rows in previewList
    const fetchStatuses = async () => {
      const newMap = {};
      await Promise.all(previewList.map(async (row) => {
        const no_akaun = row.no_akaun || row.noakaun;
        const perkara = getPerkaraString(printKategori, tarikhInbois);
        if (!no_akaun || !perkara) {
          newMap[`${no_akaun}|${perkara}`] = "Belum";
          return;
        }
        try {
          const res = await axios.get(`/api/inbois/exists`, { params: { no_akaun, perkara } });
          newMap[`${no_akaun}|${perkara}`] = res.data.exists ? "Sudah" : "Belum";
        } catch {
          newMap[`${no_akaun}|${perkara}`] = "Belum";
        }
      }));
      setStatusJanaMap(newMap);
    };
    fetchStatuses();
  };

    // Print handler: fetch inbois for month/year/zon and open print window
  const handlePrintInbois = async () => {
    if (!tarikhMula || !tarikhTamat || !printZonOnly) return;
    try {
      const res = await apiGetInboisByDateRangeZon(tarikhMula, tarikhTamat, printZonOnly);
      const inboisList = res.data || [];
      if (!inboisList.length) {
        showInfo("Tiada inbois dijumpai untuk cetakan");
        return;
      }
      // Fetch akaun data for each invoice
      const akaunDataList = await Promise.all(
        inboisList.map(async (row) => {
          try {
            const res = await apiSearchAkaun({ no_akaun: row.no_akaun || row.noakaun }, 1, 1);
            return res.data && res.data.length > 0 ? res.data[0] : {};
          } catch {
            return {};
          }
        })
      );
      // Generate QR codes for each invoice
      const publicBase = (process.env.REACT_APP_PUBLIC_BASE_URL || window.location.origin).replace(/\/$/, "");
      const qrDataUrls = await Promise.all(inboisList.map(async (row) => {
        const no_akaun = row.no_akaun || row.noakaun || "";
        if (!no_akaun) return "/build/qr.png";
        const targetUrl = `${publicBase}/qr/${encodeURIComponent(no_akaun)}`;
        try {
          return await window.QRCode.toDataURL(targetUrl, { width: 150, margin: 1 });
        } catch {
          return "/build/qr.png";
        }
      }));
      // Use the modular print HTML generator
      const printHtml = await generatePrintInboisAutoHtml(inboisList, akaunDataList, qrDataUrls);
      const printWindow = window.open("", "_blank");
      printWindow.document.write(printHtml);
      printWindow.document.close();
    } catch (err) {
      showError("Gagal memuatkan maklumat inbois untuk cetakan");
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={4} px={3}>
        <Grid container spacing={2}>
          {/* Auto Jana Inbois Section */}
          <Grid item xs={12} md={2.5} lg={2.5} sx={{ width: '25%' }}>
            {/* Auto Jana Inbois Section */}
            <Card sx={{ width: "100%" }}>
              <MDBox
                mx={2}
                mt={-3}
                py={2}
                px={2}
                variant="gradient"
                sx={pageHeaderStyles.success}
                borderRadius="lg"
                coloredShadow="info"
              >
                <MDTypography variant="h6" color="white">
                  Jana Inbois Auto
                </MDTypography>
              </MDBox>
              <MDBox p={1} mt={2}>
                <TextField
                  label="Tarikh Inbois"
                  type="date"
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  size="small"
                  value={tarikhInbois}
                  onChange={e => {
                    setTarikhInbois(e.target.value);
                    setPreviewLoaded(false);
                  }}
                />
              </MDBox>
              <MDBox px={1}>
                {/* Zon Dropdown */}
                <FormControl
                  fullWidth
                  size="small"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      backgroundColor: "#ffffffff",
                      fontSize: "0.775rem",
                      p: 1.2,
                    },
                    "& .MuiInputLabel-root": {
                      fontSize: "0.775rem",
                    },
                  }}
                >
                  <InputLabel>Zon</InputLabel>
                  <Select
                    value={printZon}
                    label="Zon"
                    onChange={(e) => {
                      setPrintZon(e.target.value);
                      setPreviewLoaded(false);
                    }}
                    fullWidth
                  >
                    {zonOptions.map((zon) => (
                      <MenuItem key={zon} value={zon}>{zon}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </MDBox>
              <MDBox p={1}>
                {/* Kategori Kutipan Dropdown */}
                <FormControl
                  fullWidth
                  size="small"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      backgroundColor: "#ffffffff",
                      fontSize: "0.775rem",
                      p: 1.2,
                    },
                    "& .MuiInputLabel-root": {
                      fontSize: "0.775rem",
                    },
                  }}
                >
                  <InputLabel>Kategori Kutipan</InputLabel>
                  <Select
                    value={printKategori}
                    label="Kategori Kutipan"
                    onChange={(e) => {
                      setPrintKategori(e.target.value);
                      setPreviewLoaded(false);
                    }}
                  >
                    {kategoriOptions.map((kategori) => (
                      <MenuItem key={kategori} value={kategori}>{kategori}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </MDBox>
              <MDBox px={2} mt={1} style={{ display: 'flex', justifyContent: 'center' }}>
                <Button
                  size="small"
                  variant="contained"
                  sx={{ ...buttonStyles.primary, mt: 1, mb: 2, px2: 0, minWidth: 0 }}
                  disabled={loadingPreview || !printZon || !printKategori}
                  onClick={async () => {
                    setLoadingPreview(true);
                    setPreviewList([]);
                    setSelectedRows([]);
                    setExpiredMap({});
                    try {
                      const user = getCurrentUser();
                      const res = await apiSearchAkaun({
                        zon: printZon,
                        kategori_kutipan: printKategori,
                        status_akaun: "Aktif",
                        bahagian_pemantau: user?.bahagian_pemantau,
                      }, 1, 1000);
                      setPreviewList(res.data || []);
                      setPreviewLoaded(true);
                      if (!res.data || res.data.length === 0) {
                        showInfo("Tiada akaun dijumpai untuk kombinasi ini");
                      } else {
                        // Determine expired rows using tarikhInbois if provided
                        let exp = {};
                        if (tarikhInbois) {
                          (res.data || []).forEach(r => {
                            if (r.tarikh_tamat) {
                              try {
                                if (new Date(tarikhInbois) > new Date(r.tarikh_tamat)) {
                                  const k = r.no_akaun || r.noakaun;
                                  exp[k] = { tarikh_tamat: r.tarikh_tamat };
                                }
                              } catch {/* ignore */}
                            }
                          });
                          setExpiredMap(exp);
                        }
                        // Auto-select only non-expired rows (or all if no tarikhInbois)
                        setSelectedRows((res.data || [])
                          .filter(r => !tarikhInbois || !exp[r.no_akaun || r.noakaun])
                          .map(r => r.no_akaun || r.noakaun));
                        // Show warning snackbar if there are expired rows
                        if (tarikhInbois && Object.keys(exp).length > 0) {
                          showInfo(`${Object.keys(exp).length} akaun telah tamat tempoh sewaan`);
                        }
                        // Refresh status jana for all rows after loading preview
                        await refreshStatusJana(res.data, printKategori, debouncedTarikhInbois || tarikhInbois);
                      }
                    } catch (e) {
                      showError("Ralat semasa mendapatkan senarai akaun");
                    } finally {
                      setLoadingPreview(false);
                    }
                  }}
                >
                  {loadingPreview ? "Loading..." : "Preview"}
                </Button>
              </MDBox>
            </Card>
            {/* Print Section */}
            <Card sx={{ width: "100%", mt: 5 }}>
              <MDBox
                mx={2}
                mt={-3}
                py={2}
                px={2}
                variant="gradient"
                sx={pageHeaderStyles.success}
                borderRadius="lg"
                coloredShadow="info"
              >
                <MDTypography variant="h6" color="white">
                  Cetak Inbois Auto
                </MDTypography>
              </MDBox>
              <MDBox p={1} mt={2}>
                <TextField
                  label="Tarikh Mula"
                  type="date"
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  size="small"
                  required
                  value={tarikhMula}
                  onChange={e => setTarikhMula(e.target.value)}
                />
              </MDBox>
              <MDBox p={1}>
                <TextField
                  label="Tarikh Tamat"
                  type="date"
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  size="small"
                  required
                  value={tarikhTamat}
                  onChange={e => setTarikhTamat(e.target.value)}
                />
              </MDBox>
              <MDBox p={1}>
                <FormControl
                  fullWidth
                  size="small"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      backgroundColor: "#ffffffff",
                      fontSize: "0.775rem",
                      p: 1.2,
                    },
                    "& .MuiInputLabel-root": {
                      fontSize: "0.775rem",
                    },
                  }}
                >
                <FormControl
                  fullWidth
                  size="small"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      backgroundColor: "#ffffffff",
                      fontSize: "0.775rem",
                      p: 1.2,
                    },
                    "& .MuiInputLabel-root": {
                      fontSize: "0.775rem",
                    },
                  }}
                ></FormControl>
                  <InputLabel>Zon</InputLabel>
                  <Select
                    value={printZonOnly}
                    label="Zon"
                    onChange={(e) => setPrintZonOnly(e.target.value)}
                  >
                    {zonOptions.map((zon) => (
                      <MenuItem key={zon} value={zon}>{zon}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </MDBox>
              <MDBox px={2} mt={1} style={{ display: 'flex', justifyContent: 'center' }}>
                <Button
                  size="small"
                  variant="contained"
                  sx={{ ...buttonStyles.primary, mt: 1, mb: 2, px2: 0, minWidth: 0 }}
                  startIcon={<PrintIcon />}
                  disabled={!tarikhMula || !tarikhTamat || !printZonOnly}
                  onClick={handlePrintInbois}
                >
                  Cetak
                </Button>
              </MDBox>
            </Card>
          </Grid>
          {/* Categories Section */}
          <Grid item xs={12} md={9.5} lg={9.5} sx={{ width: '75%' }}>
            <Card sx={{ width: "100%" }}>
              <MDBox
                mx={2}
                mt={-3}
                py={2}
                px={2}
                variant="gradient"
                sx={pageHeaderStyles.success}
                borderRadius="lg"
                coloredShadow="success"
              >
                <MDTypography variant="h6" color="white">
                  Senarai Akaun
                </MDTypography>
              </MDBox>
              <MDBox p={2}>
                {/* Preview Table */}
                {previewLoaded && previewList.length > 0 && (
                  <>
                    {/* Summary for valid vs expired rows */}
                    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:4, marginLeft:8, marginRight:8, fontSize:'0.65em', fontWeight:600 }}>
                      <span>{selectedRows.length} akaun sah dipilih{Object.keys(expiredMap).length > 0 ? ` | ${Object.keys(expiredMap).length} tamat tempoh` : ''}</span>
                      {tarikhInbois && <span style={{ opacity:0.85 }}>Tarikh Inbois: <b>{formatDDMMYYYY(tarikhInbois)}</b></span>}
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8, marginLeft: 8, marginRight: 8, fontSize: '0.7em' }}>
                      <span>
                        Zon: <b>{printZon || '-'}</b> &nbsp; | &nbsp; Kategori Kutipan: <b>{printKategori || '-'}</b>
                      </span>
                      <span style={{ minWidth: 120, textAlign: 'right' }}>
                        Jumlah Rekod: <b>{previewList.length}</b>
                      </span>
                    </div>
                    <div>
                      <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0, fontSize: '0.6em', background: '#fff', borderRadius: '16px 16px 0 0', overflow: 'hidden' }}>
                        <thead>
                          <tr style={{
                            background: 'linear-gradient(90deg, rgba(128, 137, 0, 1) 0%, rgba(239, 255, 14, 1) 50%, rgba(128, 137, 0, 1) 100%)',
                            color: '#23395d',
                            fontWeight: 600,
                            fontSize: '1em',
                            borderTopLeftRadius: 16,
                            borderTopRightRadius: 16
                          }}>
                            <th style={{ padding: '8px 6px', border: 'none', borderTopLeftRadius: 16, width: '4%' }}>
                              <input
                                type="checkbox"
                                checked={selectedRows.length > 0 && selectedRows.length === previewList.filter(r => !expiredMap[r.no_akaun || r.noakaun]).length}
                                onChange={e => {
                                  if (e.target.checked) {
                                    setSelectedRows(previewList.filter(r => !expiredMap[r.no_akaun || r.noakaun]).map(r => r.no_akaun || r.noakaun));
                                  } else {
                                    setSelectedRows([]);
                                  }
                                }}
                              />
                            </th>
                            <th style={{ padding: '8px 6px', border: 'none', width: '4%' }}>{/* Bil */}</th>
                            <th style={{ padding: '8px 6px', border: 'none', width: '14%' }}>No Akaun</th>
                            <th style={{ padding: '8px 6px', border: 'none', width: '26%' }}>Nama</th>
                            <th style={{ padding: '8px 6px', border: 'none', width: '21%' }}>Kategori Kutipan</th>
                            <th style={{ padding: '8px 6px', border: 'none', width: '10%' }}>Zon</th>
                            <th style={{ padding: '8px 6px', border: 'none', width: '9%' }}>Kadar Sewa</th>
                            <th style={{ padding: '8px 6px', border: 'none', width: '8%' }}>Tamat</th>
                            <th style={{ padding: '8px 6px', border: 'none', borderTopRightRadius: 16, width: '8%' }}>Status Jana</th>
                          </tr>
                        </thead>
                        <tbody>
                          {previewList.map((row, idx) => {
                            const rowKey = row.no_akaun || idx;
                            const expiredInfo = expiredMap[row.no_akaun || row.noakaun];
                            const isExpired = !!expiredInfo;
                            return (
                              <tr
                                key={rowKey}
                                style={{ borderBottom: '1px solid #e0e0e0', background: isExpired ? '#ffe5e5' : (idx % 2 === 0 ? '#fff' : '#fafbfc') }}
                                title={isExpired && row.tarikh_tamat ? `Lewat tarikh tamat (${formatDDMMYYYY(row.tarikh_tamat)})` : ''}
                              >
                                <td style={{ padding: '6px 6px', textAlign: 'center', border: 'none' }}>
                                  <input
                                    type="checkbox"
                                    checked={selectedRows.includes(rowKey)}
                                    onChange={e => {
                                      if (e.target.checked) {
                                        setSelectedRows(prev => [...prev, rowKey]);
                                      } else {
                                        setSelectedRows(prev => prev.filter(k => k !== rowKey));
                                      }
                                    }}
                                  />
                                </td>
                                <td style={{ padding: '6px 6px', textAlign: 'center', border: 'none' }}>{idx + 1}</td>
                                <td style={{ padding: '6px 6px', textAlign: 'center', border: 'none' }}>{row.no_akaun}</td>
                                <td style={{ padding: '6px 6px', textAlign: 'left', border: 'none' }}>{row.nama}</td>
                                <td style={{ padding: '6px 6px', textAlign: 'center', border: 'none' }}>{row.kategori_kutipan}</td>
                                <td style={{ padding: '6px 6px', textAlign: 'center', border: 'none' }}>{row.zon}</td>
                                <td style={{ padding: '6px 6px', textAlign: 'right', border: 'none' }}>{row.kadar_sewa}</td>
                                <td style={{ padding: '6px 6px', textAlign: 'center', border: 'none' }}>
                                  {row.tarikh_tamat ? (
                                    <span style={{
                                      display: 'inline-block',
                                      padding: '2px 6px',
                                      borderRadius: 12,
                                      fontWeight: 600,
                                      fontSize: '0.9em',
                                      background: isExpired ? '#c62828' : '#e0e0e0',
                                      color: isExpired ? '#fff' : '#424242'
                                    }}>
                                      {formatDDMMYYYY(row.tarikh_tamat)}
                                    </span>
                                  ) : (
                                    <span style={{ opacity: 0.4 }}>-</span>
                                  )}
                                </td>
                                <td style={{ padding: '6px 6px', textAlign: 'center', border: 'none' }}>
                                  {(() => {
                                    const no_akaun = row.no_akaun || row.noakaun;
                                    const perkara = getPerkaraString(printKategori, tarikhInbois);
                                    const key = `${no_akaun}|${perkara}`;
                                    const status = statusJanaMap[key];
                                    if (status === "Sudah") {
                                      return <CheckCircleIcon style={{ color: '#43a047' }} titleAccess="Sudah" />;
                                    } else if (status === "Belum") {
                                      return <CancelIcon style={{ color: '#e53935' }} titleAccess="Belum" />;
                                    } else {
                                      return "...";
                                    }
                                  })()}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}
              </MDBox>
              {/* Snackbar for feedback */}
              {/* Loading bar and Snackbar */}
              <div style={{ position: 'relative', minHeight: 36, marginBottom: 8 }}>
                {isJanaLoading && (
                  <div style={{ width: '100%', height: 8, background: '#eee', borderRadius: 4, overflow: 'hidden', margin: '0 auto' }}>
                    <div style={{ width: '100%', height: '100%', background: 'linear-gradient(90deg, #fbc02d 0%, #43a047 100%)', animation: 'loadingBarAnim 1.2s linear infinite' }} />
                  </div>
                )}
                <style>{`
                  @keyframes loadingBarAnim {
                    0% { transform: translateX(-100%); }
                    100% { transform: translateX(100%); }
                  }
                `}</style>
              </div>
              <MDBox px={2} mt={1} style={{ display: 'flex', justifyContent: 'center' }}>
                <Button
                  size="small"
                  variant="contained"
                  sx={{ ...buttonStyles.save, mt: 1, mb: 2, px2: 0, minWidth: 0 }}
                  onClick={handleBulkJanaInbois}
                  disabled={previewList.length === 0}
                >
                  Jana
                </Button>
             </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
    </DashboardLayout>
  );
}
